import { useAutoApprovedQualifications } from "@src/appV2/Licenses/useAutoApprovedQualifications";
import { isActiveLicense } from "@src/appV2/Licenses/utils";
import {
  useWorkerQualificationsWithLicenses,
  type WorkerQualificationWithLicenses,
} from "@src/appV2/redesign/Roles/useWorkerQualificationsWithLicenses";
import { useMemo } from "react";

export type ActiveQualificationWithLicenses = Pick<
  WorkerQualificationWithLicenses,
  "qualification" | "licenses"
>;

interface UseActiveQualificationsWithLicensesResult {
  data: ActiveQualificationWithLicenses[];
  isLoading: boolean;
}

export function useActiveQualificationsWithLicenses(): UseActiveQualificationsWithLicensesResult {
  const { data: qualificationsWithLicenses, isLoading: isLoadingQualificationsWithLicenses } =
    useWorkerQualificationsWithLicenses();
  const autoApprovedQualifications = useAutoApprovedQualifications();

  const activeQualificationsWithLicenses = useMemo(() => {
    const activeQualificationsWithLicenses: ActiveQualificationWithLicenses[] = [];

    for (const { qualification, licenses } of qualificationsWithLicenses) {
      const isAutoApproved = autoApprovedQualifications.has(qualification.attributes.name);
      const activeLicenses = licenses.filter((license) => isActiveLicense(license));

      // If qualification is auto approved or has active licenses, we add it to the list
      if (isAutoApproved || activeLicenses.length > 0) {
        activeQualificationsWithLicenses.push({
          qualification,
          licenses: activeLicenses,
        });
      }
    }

    return activeQualificationsWithLicenses;
  }, [qualificationsWithLicenses, autoApprovedQualifications]);

  return {
    data: activeQualificationsWithLicenses,
    isLoading: isLoadingQualificationsWithLicenses,
  };
}
